<template>
  <v-dialog
    v-model="dialog"
    :max-width="maxWidth"
  >
    <template #activator="{ on, attrs }">
      <ActivatorButton
        v-if="buttonTitle"
        :parent-attrs="attrs"
        :parent-on="on"
        :icon-or-title="buttonTitle"
        :tooltip-text="title"
        :disabled="disabled"
        :style="{ display: visible ? 'flex' : 'none' }"
        @activate="onButtonActivate"
      />
    </template>

    <v-form ref="form">
      <v-card>
        <v-card-title>
          {{ formTitle }}
        </v-card-title>

        <v-card-text>
          <v-container>
            <v-row dense>
              <v-col>
                <v-text-field
                  v-model="editedItem.name"
                  label="Username"
                  :rules="nameRules"
                  @input="checkUser"
                />
              </v-col>
              <v-col v-if="!editMode">
                <v-text-field
                  v-model="editedItem.password"
                  :append-icon="show ? 'mdi-eye' : 'mdi-eye-off'"
                  :rules="passwordRules"
                  :type="show ? 'text' : 'password'"
                  counter
                  hint="At least 6 characters"
                  label="Password"
                  @click:append="show = !show"
                />
              </v-col>
              <v-col v-if="!editMode">
                <v-text-field
                  v-model="editedItem.password2"
                  :append-icon="show ? 'mdi-eye' : 'mdi-eye-off'"
                  :rules="password2Rules"
                  :type="show ? 'text' : 'password'"
                  counter
                  hint="At least 6 characters"
                  label="Repeat password"
                  @click:append="show = !show"
                />
              </v-col>
            </v-row>

            <v-row dense>
              <v-col>
                <v-text-field
                  v-model="editedItem.fullName"
                  label="Full Name"
                />
              </v-col>
              <v-col>
                <v-text-field
                  v-model="editedItem.email"
                  :rules="[rules.required, rules.min, emailCheck]"
                  label="Email"
                  @input="checkUser"
                />
              </v-col>
            </v-row>

            <v-row dense>
              <v-col>
                <v-text-field
                  v-model="editedItem.company"
                  label="Company"
                />
              </v-col>
              <v-col v-if="isAdmin">
                <v-checkbox
                  v-model="editedItem.active"
                  label="Active"
                />
              </v-col>
            </v-row>

            <template v-if="isAdmin">
              <v-row dense>
                <v-col>
                  <v-select
                    v-model="editedItem.privileges"
                    :items="PRIVILEGES"
                    item-value="name"
                    item-text="name"
                    label="Privileges"
                    multiple
                  />
                </v-col>
              </v-row>
              <v-row dense>
                <v-col>
                  <v-select
                    v-model="editedItem.groups"
                    :items="groups"
                    item-value="name"
                    item-text="name"
                    return-object
                    label="Groups"
                    multiple
                  />
                </v-col>
              </v-row>
              <v-row dense>
                <v-col>
                  <v-select
                    v-model="editedItem.roles"
                    :items="roles"
                    item-value="name"
                    item-text="name"
                    return-object
                    label="Roles"
                    multiple
                  />
                </v-col>
              </v-row>
            </template>
          </v-container>
        </v-card-text>

        <v-progress-linear
          v-if="uploading"
          indeterminate
        />

        <div
          v-if="message"
          style="color: red"
        >
          {{ message }}
        </div>

        <v-card-actions>
          <v-spacer />

          <v-btn
            @click="close"
            aria-label="Cancel button"
          >
            Cancel
          </v-btn>
          <v-btn
            @click="onOk"
            aria-label="Save button"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>
<script>
import DialogBase from '@/components/dialogs/DialogBase.vue'
import md5 from 'md5'
import { mapGetters } from 'vuex'
import { AUTH_URL, getErrorMessage } from '@/main'
import ActivatorButton from '@/components/ActivatorButton.vue'

export default {
  components: { ActivatorButton },
  mixins: [DialogBase],

  props: {
    item: {
      type: [Object, null],
      default: null,
    },
  },

  data: () => ({
    editedItem: {
      name: null,
      password: null,
      password2: null,
      fullName: null,
      email: null,
      company: null,
      active: true,
      privileges: [],
      groups: [],
      roles: [],
    },
    defaultItem: {
      name: null,
      password: null,
      password2: null,
      fullName: null,
      email: null,
      company: null,
      active: true,
      privileges: [],
      groups: [],
      roles: [],
    },
    groups: null,
    roles: null,
    show: false,
    rules: {
      required: (value) => !!value || 'Required',
      min: (v) => v != null && (v.length >= 6 || 'Min 6 characters'),
    },
    checks: {},
  }),

  computed: {
    ...mapGetters(['PRIVILEGES', 'isAdmin']),
    OBJECTS_TYPE() {
      return 'users'
    },
    formTitle() {
      return this.editMode ? 'Edit user' : 'Create user'
    },
    preparedUser() {
      return {
        id: this.editMode ? this.item.id : null,
        name: this.editedItem.name,
        passwordHash: this.editedItem.password ? md5(this.editedItem.password) : null,
        fullName: this.editedItem.fullName,
        email: this.editedItem.email,
        company: this.editedItem.company,
        active: this.editedItem.active,
        privileges: this.editedItem.privileges,
        groups: this.editedItem.groups.map((g) => g.name),
        roles: this.editedItem.roles.map((r) => r.name),
      }
    },
    nameRules() {
      return [this.rules.required, this.rules.min, this.nameCheck]
    },
    passwordRules() {
      return [this.rules.required, this.rules.min]
    },
    password2Rules() {
      return [this.rules.required, this.rules.min, this.passwordsMatchRule]
    },
    passwordsMatchRule() {
      return this.editedItem.password === this.editedItem.password2 || 'Passwords do not match'
    },
    nameCheck() {
      return (this.editMode && this.editedItem.name === this.item.name) || !this.checks.name || this.checks.name
    },
    emailCheck() {
      return (this.editMode && this.editedItem.email === this.item.email) || !this.checks.email || this.checks.email
    },
    editMode() {
      return !!this.item
    },
  },

  watch: {
    dialog(val) {
      if (val) {
        if (this.editMode) this.editedItem = Object.assign({}, this.item)
        else this.editedItem = Object.assign({}, this.defaultItem)
        this.$axios.get(`${AUTH_URL}/groups`).then((response) => {
          this.groups = response.data
          if (this.editMode) this.editedItem.groups = this.item.groups
        })
        this.$axios.get(`${AUTH_URL}/roles`).then((response) => {
          this.roles = response.data
          if (this.editMode) this.editedItem.roles = this.item.roles
        })
      }
    },
  },

  methods: {
    checkUser() {
      this.$axios
        .get(
          `${AUTH_URL}/users/check?name=${encodeURIComponent(this.editedItem.name)}&email=${encodeURIComponent(
            this.editedItem.email,
          )}`,
        )
        .then((response) => {
          this.checks = response.data
        })
    },
    onOk() {
      if (this.$refs.form.validate()) {
        this.uploading = true
        if (this.editMode) {
          this.$axios
            .put(`${AUTH_URL}/${this.OBJECTS_TYPE}/${this.item.id}`, this.preparedUser)
            .then(() => {
              this.$emit('userUpdated', this.editedItem)
              this.close()
            })
            .catch((error) => {
              this.uploading = false
              this.message = getErrorMessage(error)
            })
        } else {
          this.$axios
            .post(`${AUTH_URL}/${this.OBJECTS_TYPE}`, this.preparedUser)
            .then((response) => {
              this.$emit('userCreated', response.data)
              this.close()
            })
            .catch((error) => {
              this.uploading = false
              this.message = getErrorMessage(error)
            })
        }
      }
    },
  },
}
</script>
